//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { APR, otAssert, toRoot365 } from '@shared/conversions'

import { IPoolBaseAbi, PoolControllerFlexAbi, PoolFlexAbi } from '@shared/Abis'

import { ProviderWritable } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'

export const setDailyInterestRateForFlexTx =
  (props: { poolAddr: EthAddr; interestRate: APR }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.POOL_SET_FLEX_INTEREST_RATE

    try {
      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.BORROWER)
      const { poolAddr, interestRate } = props
      logOT.debug('setFlexInterestRateSaga', poolAddr)

      await setDailyInterestRateForFlex(
        webSocket,
        signer as Signer,
        poolAddr,
        Number(interestRate),
        chainConfigurationName
      )
      dispatch(poolActions.setFlexInterestRateSuccess(props))
    } catch (e: any) {
      dispatch(poolActions.setFlexInterestRateFail(props))
      await dispatchError(dispatch, e, activity, 1019)
    }
  }
export const setDailyInterestRateForFlex = async (
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  poolAddr: EthAddr,
  interestRate: number,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('setDailyInterestRateForFlex ')
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.POOL_SET_FLEX_INTEREST_RATE, poolAddr, uuid, chainConfigurationName)
  )
  const providerWritable = borrowerSigner.provider as ProviderWritable
  logOT.debug('QQsetDailyInterestRateForFlex ', interestRate)
  otAssert(interestRate > 50 && interestRate < 5000) // more than 0.5% less 50% interest rate

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, providerWritable)
  const poolType = Number(await poolBaseContract.poolType())
  if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, borrowerSigner)
    const poolControllerAddr = await poolFlexContract.poolController()
    const poolControllerFlex = new Contract(poolControllerAddr, PoolControllerFlexAbi, borrowerSigner)

    const originationFeeRet = await poolControllerFlex.originationFee()
    logOT.debug('originationFeeRet', originationFeeRet)
    const originationFee = Number(originationFeeRet) / 10000
    const interestRateDecimal = interestRate / 10000
    const paidInterestRate = interestRateDecimal - originationFee

    logOT.debug('\n\n\naccureDailyInterest', typeof interestRate, paidInterestRate)
    const root = toRoot365(Math.round(paidInterestRate * 10000))

    const tx2 = await poolFlexContract.setDailyInterestRate(interestRate, root.toString())

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_SET_FLEX_INTEREST_RATE,
        poolAddr,
        uuid,
        tx2.hash as TxHash,
        chainConfigurationName
      )
    )

    tx2.wait(1)

    logOT.debug('setDailyInterestRateForFlex success', poolAddr)
  } else {
    throw new Error('Pool is not flexible rate')
  }
}
